import {
  LibraryComplexitiesMap,
  LibraryComponent,
  LibraryComponentApi,
  LibraryComponentCreateApi,
  LibraryComponentEntry,
  LibraryComponentEntryPayload,
  LibraryComponentEntryRequest,
  LibraryComponentEntryUpdateRequest,
  LibraryDuplicateEntryRequest,
} from '@app/features/library-management/store/models/component'
import {
  LibraryCreateComponentActionPayload,
  LibraryCreateDeliverableEntryActionPayload,
  LibraryCreateTPCActionPayload,
  LibraryDuplicateDeliverableEntryActionPayload,
} from '@app/features/library-management/store/models/modals'
import {
  LibraryDeliverable,
  LibraryDeliverableEntryRequest,
} from '@app/features/library-management/store/models/deliverable'
import {
  LibraryDuplicateEntryPayload,
  LibraryManagementFilters,
  LibraryRateCard,
} from '@app/features/library-management/store'

import { ComplexitiesList } from '@app/core/model/enums/complexity.enum'
import { GenericFilterValue } from '@app/core/model/filter-option.interface'
import { Injectable } from '@angular/core'
import { LibraryItemType } from '@app/features/library-management/store/enums'
import { LibraryManagementFolder } from '@app/features/library-management/store/models/library-folders'
import { LibraryManagementItem } from '@app/features/library-management/store/models/library-items'
import { LibraryManagementMappingService } from './library-management-mapping.service'
import { ThirdPartyCost } from '@app/core/model/third-party-cost.model'
import { TimePeriod } from '@app/core/model/enums/time-period.enum'
import { getPlainHttpParams } from '@app/shared/utils/utils'
import { plainToInstance } from 'class-transformer'

@Injectable({ providedIn: 'root' })
export class LibraryManagementApiMappingService {
  constructor(private libraryMappingService: LibraryManagementMappingService) {}

  mapLibraryItemToApi = (item: LibraryManagementItem) => ({ id: item.id, name: item.name, itemType: item.itemType })

  mapLibraryTcpToApi = (item: LibraryManagementItem) => ({
    id: item.id,
    name: item.name,
    itemType: item.itemType,
    createdBy: item.createdBy,
    unitCost: item.unitCost,
    version: item.version,
    cascadeOptions: { cascadeLibrary: item.cascadeLibrary, cascadeScope: false },
  })

  mapFiltersToApi = (filters: LibraryManagementFilters) => ({
    ...filters,
    ratecards: this.mapGenericFilterToJoinedString(filters.ratecards),
    members: this.mapGenericFilterToJoinedString(filters.members),
    createdDates: filters.createdDates?.length
      ? filters.createdDates.map((date) => this.getDateByTimeframe(date.id)).join(',')
      : undefined,
    disciplines: this.mapGenericFilterToJoinedString(filters.disciplines),
  })

  mapFolderToApi = (folder, parentFolder?) => ({
    ...folder,
    itemType: LibraryItemType.FOLDER,
    libraryFolder: parentFolder?.id
      ? { id: parentFolder.id, name: parentFolder.name, itemType: LibraryItemType.FOLDER }
      : undefined,
  })

  mapDeliverableToApi = (deliverable: LibraryDeliverable, folder?: LibraryManagementFolder) =>
    getPlainHttpParams({
      id: deliverable.id,
      name: deliverable.name,
      description: deliverable.description,
      extraDescription: deliverable.extraDescription,
      fixedPricing: deliverable.fixedPricing,
      itemType: deliverable.itemType,
      internalNote: deliverable.internalNote,
      libraryFolder: folder ? { id: folder.id, itemType: LibraryItemType.FOLDER, name: folder.name } : undefined,
    }) as unknown as LibraryDeliverable

  mapDeliverableEntryToApi = (entry: LibraryCreateDeliverableEntryActionPayload): LibraryDeliverableEntryRequest => ({
    description: entry.description,
    extraDescription: entry.extraDescription,
    internalNote: entry.internalNote,
    fixedCostEditable: entry.fixedCostEditable,
    thirdPartyCosts: [],
    sections: [],
    libraryComponents: [],
    feeItems: [],
    discipline: { id: entry.discipline.id, name: entry.discipline.name },
    rateCardVersion: { ...entry.rateCard },
  })

  mapDeliverableEntryDuplicatePayloadApi = ({
    discipline,
    rolesMapping = {},
  }: LibraryDuplicateDeliverableEntryActionPayload): LibraryDuplicateEntryPayload => ({
    newDiscipline: {
      active: discipline.active,
      id: discipline.id,
      name: discipline.name,
      nonTranslatedName: discipline.nonTranslatedName,
    },
    rolesMapping,
  })

  mapComponentToApi = (component: LibraryComponent): LibraryComponentApi => ({
    id: component.id,
    name: component.name,
    itemType: LibraryItemType.COMPONENT,
  })

  mapComponentToCreateApi = (
    component: LibraryCreateComponentActionPayload,
    currency: string,
    folder: LibraryManagementFolder
  ): LibraryComponentCreateApi => {
    const isFixedFeeNoRoles = component.fixedPricing && !component.fixedFeeHasRoles
    return {
      name: component.name,
      fixedPricing: component.fixedPricing,
      itemType: LibraryItemType.COMPONENT,
      libraryFolder: folder ? { id: folder.id, itemType: LibraryItemType.FOLDER, name: folder.name } : undefined,
      libraryComponentEntries: plainToInstance(LibraryComponentEntry, [
        {
          rateCardVersion: component.rateCard,
          componentComplexities: ComplexitiesList.reduce(
            (complexities, key) => ({
              ...complexities,
              [key]: {
                complexity: key,
                description: component.description,
                extraDescription: component.extraDescription,
                fixedCost:
                  component.price && (!isFixedFeeNoRoles || key === 'L') ? { currency, amount: component.price } : null,
                costAmount: !isFixedFeeNoRoles || key === 'L' ? component.cost : null,
                markup: !isFixedFeeNoRoles || key === 'L' ? +component.markup : null,
              },
            }),
            {} as LibraryComplexitiesMap
          ),
          fixedPricing: component.fixedPricing,
          fixedFeeEditable: component.fixedPricing ? component.fixedFeeEditable : false,
          fixedFeeHasRoles: component.fixedPricing ? component.fixedFeeHasRoles : false,
          discipline: { id: component.discipline.id, name: component.discipline.name },
        },
      ]),
    }
  }

  mapComponentEntryToApi = (payload: LibraryComponentEntryPayload, currency: string): LibraryComponentEntryRequest => {
    const isFixedFeeNoRoles = payload.fixedPricing && !payload.fixedFeeHasRoles
    return {
      generalDescription: payload.description,
      fixedFeeEditable: payload.fixedFeeEditable,
      fixedFeeHasRoles: payload.fixedFeeHasRoles,
      discipline: { id: payload.discipline.id, name: payload.discipline.name },
      rateCardVersion: plainToInstance(LibraryRateCard, payload.rateCard),
      componentComplexities: ComplexitiesList.reduce(
        (complexities, key) => ({
          ...complexities,
          [key]: {
            complexity: key,
            description: payload.description,
            extraDescription: payload.extraDescription,
            fixedCost: isFixedFeeNoRoles && key === 'L' ? { currency, amount: +(payload as any).price || 0 } : null,
            costAmount:
              isFixedFeeNoRoles && key === 'L'
                ? +(payload as any).cost || 0
                : isFixedFeeNoRoles
                  ? null
                  : +payload.complexities?.[key]?.cost || 0,
            markup:
              isFixedFeeNoRoles && key === 'L'
                ? +(payload as any).markup || 0
                : isFixedFeeNoRoles
                  ? null
                  : +payload.complexities?.[key]?.markup || 0,
          },
        }),
        {} as LibraryComplexitiesMap
      ),
    }
  }

  mapComponentEntryToUpdateApi = (payload: LibraryComponentEntry): LibraryComponentEntryUpdateRequest => ({
    id: payload.id,
    name: payload.name,
    fixedPricing: payload.fixedPricing,
    fixedFeeEditable: payload.fixedFeeEditable,
    fixedFeeHasRoles: payload.fixedFeeHasRoles,
    versionNumber: payload.versionNumber,
    rateCardVersion: plainToInstance(LibraryRateCard, payload.rateCardVersion),
    componentComplexities: ComplexitiesList.reduce((complexities, key) => {
      const complexity = payload.componentComplexities?.[key]

      return {
        ...complexities,
        [key]: {
          complexity: key,
          aliasName: complexity?.aliasName,
          description: complexity?.description,
          extraDescription: complexity?.extraDescription,
          internalNote: complexity?.internalNote,
          fixedCost: !payload.fixedFeeHasRoles ? complexity?.fixedCost : null,
          costAmount: complexity?.costAmount,
          roles: complexity?.roles
            ? complexity?.roles.map((role) => this.libraryMappingService.mapRoleToComplexityRole(role))
            : [],
          scenarioFormulas: complexity?.scenarioFormulas,
        },
      }
    }, {} as LibraryComplexitiesMap),
  })

  mapComponentEntryDuplicatePayloadApi = (payload: LibraryComponentEntryPayload): LibraryDuplicateEntryRequest => ({
    rolesMapping: payload.rolesMapping || null,
    newDiscipline: {
      id: payload.discipline.id,
      name: payload.discipline.name,
      nonTranslatedName: payload.discipline.nonTranslatedName,
      active: payload.discipline.active,
    },
  })

  mapThirdPartyCostToApi = (tpc: LibraryCreateTPCActionPayload, folder?: LibraryManagementFolder) => ({
    name: tpc.name,
    description: tpc.description,
    itemType: LibraryItemType.THIRD_PARTY_COST,
    unitCost: +tpc.unitCost,
    sellingPrice: +tpc.sellingPrice,
    markupPercentage: tpc.markup ? +tpc.markup / 100 : 0,
    group: tpc.group && { id: tpc.group.id, type: tpc.group.groupType },
    formula: tpc.formula && { id: tpc.formula.id, type: tpc.formula.getName },
    libraryFolder: folder ? { id: folder.id, itemType: LibraryItemType.FOLDER, name: folder.name } : undefined,
  })

  mapThirdPartyCostToUpdateApi = (tpc: ThirdPartyCost) =>
    plainToInstance(ThirdPartyCost, {
      id: tpc.id,
      name: tpc.name,
      description: tpc.description,
      itemType: LibraryItemType.THIRD_PARTY_COST,
      unitCost: +tpc.unitCost,
      sellingPrice: +tpc.sellingPrice,
      group: tpc.group && { id: tpc.group.id, type: tpc.group.groupType },
      formula: tpc.formula && Object.keys(tpc.formula).length != 0 ? tpc.formula : null,
      unitType: tpc.unitType,
      markupPercentage: tpc.markupPercentage ? +tpc.markupPercentage / 100 : 0,
      createdBy: tpc.createdBy,
      version: tpc.version,
    })

  private mapGenericFilterToJoinedString = (arr: GenericFilterValue[]) =>
    arr?.length ? arr.map((item) => item.id).join(',') : undefined

  private getDateByTimeframe(timeframe: TimePeriod) {
    const date = new Date()

    switch (timeframe) {
      case TimePeriod.WEEK:
        date.setDate(date.getDate() - 7)
        break
      case TimePeriod.MONTH:
        date.setMonth(date.getMonth() - 1)
        break
      case TimePeriod.HALF_YEAR:
        date.setMonth(date.getMonth() - 6)
        break
      case TimePeriod.YEAR:
        date.setFullYear(date.getFullYear() - 1)
    }

    return date.toISOString()
  }
}
